/**
 * useEventTemplates Hook
 *
 * Manages calendar event templates via the API. Provides CRUD operations
 * and a helper to create a template from an existing event.
 */

import { useState, useEffect, useCallback } from 'react';
import { api, CalendarEventTemplateDetail, CalendarEventTemplateInput } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import type { EventInstance } from '../types';

export function useEventTemplates() {
  const [templates, setTemplates] = useState<CalendarEventTemplateDetail[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.listCalendarEventTemplates();
      setTemplates(result.templates);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to load templates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const createTemplate = useCallback(async (input: CalendarEventTemplateInput) => {
    const created = await api.createCalendarEventTemplate(input);
    setTemplates((prev) => [...prev, created]);
    return created;
  }, []);

  const updateTemplate = useCallback(
    async (id: string, input: Partial<CalendarEventTemplateInput>) => {
      const updated = await api.updateCalendarEventTemplate(id, input);
      setTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
      return updated;
    },
    [],
  );

  const deleteTemplate = useCallback(async (id: string) => {
    await api.deleteCalendarEventTemplate(id);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const createTemplateFromEvent = useCallback(
    async (event: EventInstance, name: string) => {
      const startMs = new Date(event.startAt).getTime();
      const endMs = new Date(event.endAt).getTime();
      const durationMinutes = Math.round((endMs - startMs) / 60000);

      const input: CalendarEventTemplateInput = {
        name,
        title: event.title,
        description: event.description || undefined,
        location: event.location || undefined,
        durationMinutes: durationMinutes > 0 ? durationMinutes : 60,
        isAllDay: event.isAllDay,
        status: event.status,
        color: event.color || undefined,
        url: event.url || undefined,
        calendarId: event.calendarId,
        isRecurring: event.isRecurring,
        rrule: event.rrule || undefined,
        reminders: event.reminders,
        tags: event.tags,
      };

      return createTemplate(input);
    },
    [createTemplate],
  );

  return {
    templates,
    loading: loading,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    createTemplateFromEvent,
  };
}
